import { Auth } from '@angular/fire/auth'
import { Injectable } from '@angular/core';
import { Observable, pluck } from 'rxjs';
import { collection, collectionData, collectionGroup, deleteField, doc, docData, DocumentReference, Firestore, orderBy, query, runTransaction, serverTimestamp, updateDoc, where } from '@angular/fire/firestore';

import { Operation, SegmentationWithId, Score, Team, Publication, Fresh, Checklist, volumetryType } from '@innersight-os/shared/firestore-interfaces';
import { ActionsService } from '../actions/actions.service';
import { MedDevSoftwareVerion } from '@innersight-os/shared/util';
import { DocumentData } from '@firebase/firestore-types';

interface SegmentationUpdate {
  title?: string;
  description?: string;
  score?: Score;
}

@Injectable({
  providedIn: 'root'
})
export class SegmentationsService {
  constructor(
    private actions: ActionsService,
    private auth: Auth,
    private db: Firestore,
  ) {}

  getSegmentations = (operationId: string, uploadId: string, volumeId: string): Observable<SegmentationWithId[]> =>
    collectionData(
      query<SegmentationWithId, DocumentData>(
        // eslint-able-next-line @typescript-eslint/no-explicit-any
        collection(this.db, `operations/${operationId}/uploads/${uploadId}/volumes/${volumeId}/segmentations`) as any,
        orderBy('createdAt', 'desc'),
      ),
      {idField: 'id'},
    ) as Observable<SegmentationWithId[]>

  getSegmentationScore = (operationId: string, uploadId: string, volumeId: string, segmentationId: string): Observable<Score> =>
    docData<SegmentationWithId>(
      doc(this.db, `operations/${operationId}/uploads/${uploadId}/volumes/${volumeId}/segmentations/${segmentationId}`) as DocumentReference<SegmentationWithId>
    ).pipe(
      pluck('score')
    )

  getSegmentationsForOperation = (operationId: string): Observable<SegmentationWithId[]> =>
    collectionData(
      query<SegmentationWithId, DocumentData>(
        // eslint-able-next-line @typescript-eslint/no-explicit-any
        collectionGroup(this.db, 'segmentations') as any,
        where('operationId', '==', operationId),
      ),
      {idField: 'id'},
    ) as Observable<SegmentationWithId[]>

  hide = (operationId: string, uploadId: string, volumeId: string, id: string) =>
    updateDoc(
      doc(this.db, `operations/${operationId}/uploads/${uploadId}/volumes/${volumeId}/segmentations/${id}`),
      {hideInSegment: true},
    )

  update = (operationId: string, uploadId: string, volumeId: string, id: string, update: SegmentationUpdate) =>
    updateDoc(
      doc(this.db, `operations/${operationId}/uploads/${uploadId}/volumes/${volumeId}/segmentations/${id}`),
      // eslint-able-next-line @typescript-eslint/no-explicit-any
      update as any,
    )

  // TODO: consider moving action methods to ActionsService

  async segmentationSubmitted(
    operationId: string,
    uploadId: string,
    volumeId: string,
    segmentationId: string,
    checklist: string[],
    ) {
    try {
      return await runTransaction(this.db, async transaction => {
        const opDocRef = doc(this.db, `operations/${operationId}`)
        const opDoc = await transaction.get(opDocRef)
        const opData = opDoc.data() as Operation
        const teamDocRef = doc(this.db, `teams/${opData.team}`)
        const teamDoc = await transaction.get(teamDocRef)
        const {managers} = teamDoc.data() as Team
        this.actions.recordAction(undefined, operationId, {type: 'segmentationCompleted'}, transaction)

        const checklistRef = doc(collection(this.db, `operations/${operationId}/checklists`))
        const checklistData: Fresh<Checklist> = {
          checklistType: 'MFG',
          checklist,
          createdAt: serverTimestamp(),
          createdBy: this.auth.currentUser.uid,
          operationId,
          uploadId,
          volumeId,
          segmentationId,
        }
        transaction.set(checklistRef, checklistData)

        managers.forEach(manager => this.actions.recordAction(manager, operationId, {type: 'reviewRequested'}, transaction))
      })
    } catch(err){
      console.error('transaction failed', err);
    }
  }

  async submitReview(
    segmenterId: string,
    operationId: string,
    action: 'segmentationApproved' | 'segmentationRequested') {
    try {
      await runTransaction(this.db, async transaction =>
        this.actions.recordAction(segmenterId, operationId, {type: action}, transaction)
      )
    } catch(err) {
      console.error('transaction failed', err);
    }
    // nb: alert is used rather than scrapping Publish button because Publish is used to Revoke and is gated by mesh conditions
    if (action === 'segmentationApproved') alert("You must now click the `Publish` button for the same segmentation you just approved.")
  }

  async publishMeshToOperation(
    operationId: string,
    uploadId: string,
    volumeId: string,
    segmentationId: string,
    meshId: string,
    empty: boolean,
    checklist: string[],
    ) {
    try {
      return await runTransaction(this.db, async transaction => {
        const opRef = doc(this.db, 'operations', operationId)
        const publication: Fresh<Publication> = {
          publishedAt: serverTimestamp(),
          uploadId,
          volumeId,
          segmentationId,
          meshId,
          empty,
          medDevSoftwareVerion: MedDevSoftwareVerion,
        }
        transaction.update(opRef, {publication})

        const checklistRef = doc(collection(this.db, `operations/${operationId}/checklists`))
        const checklistData: Fresh<Checklist> = {
          checklistType: 'QC',
          checklist,
          createdAt: serverTimestamp(),
          createdBy: this.auth.currentUser.uid,
          operationId,
          uploadId,
          volumeId,
          segmentationId,
        }
        transaction.set(checklistRef, checklistData)

        this.actions.recordAction(undefined, operationId, {type: 'published'}, transaction)

      })
    } catch(err){
      console.error('transaction failed', err);
    }
  }

  async revokeMeshToOperation(operationId: string) {
    try {
      return await runTransaction(this.db, async transaction => {
        const opRef = doc(this.db, 'operations', operationId)
        const opData = {
          downloadableModelDatasetReady: false,
          publication: deleteField(),
        }
        transaction.update(opRef, opData)
        this.actions.recordAction(undefined, operationId, {type: 'publicationRevoked'}, transaction)
      })
    } catch(err){
      console.error('transaction failed', err);
    }
  }
  async calculateVolumetry(operationId: string, uploadId: string, volumeId: string, segmentationId: string, volumetry: volumetryType[]) {
    try {
      return await runTransaction(this.db, async transaction => {
        const segmentationRef = doc(this.db, `operations/${operationId}/uploads/${uploadId}/volumes/${volumeId}/segmentations/${segmentationId}`)
        const segmentation = await transaction.get(segmentationRef)
        const segmentationData = segmentation.data() as SegmentationWithId
        segmentationData.volumetry = volumetry
        await transaction.update(segmentationRef, {volumetry: segmentationData.volumetry})
      })
    } catch(err){
      console.error('transaction failed', err);
    }
  }
}
